// src/components/leader/DancerPicker.js
//
// Dancer selection shared by the registration wizard and the choreography detail
// (audit FL-08, FL-18):
// * every dancer is a <label> wrapping a real checkbox - tapping the box or the card works,
//   and it is reachable with the keyboard;
// * only dancers who may dance in the category are offered (requirement 2.6); a dancer
//   who no longer fits (e.g. after a category change) stays visible in red and can only
//   be removed;
// * search, "only selected" filter and the counter "Vybráno n (min. 4, max. 50)";
// * the same age rules as the backend (GET /api/meta: year of the competition, tolerance
//   -2/+1 year, Baby never under 3, Senioři 25+, at most 50 % with tolerance).
import React, { useMemo, useState } from 'react';
import { AlertTriangle, Search } from 'lucide-react';
import { ageCategoryLabel } from '../../constants/categories';
import { DEFAULT_AGE_RULES, useMeta } from '../../contexts/MetaContext';
import { plural } from '../../utils/format';

export const MIN_DANCERS = 4;
export const MAX_DANCERS = 50;

/** "6–12 let, tolerance -2/+1 rok" for the hint above the list. */
export function ruleText(ageCategory, rules = DEFAULT_AGE_RULES) {
  const rule = rules?.categories?.[ageCategory];
  if (!rule) return '';
  const range = rule.max == null ? `${rule.min} let a více` : `${rule.min}–${rule.max} let`;
  if (!rule.toleranceYounger && !rule.toleranceOlder) return `${range}, bez věkové tolerance`;
  const parts = [];
  if (rule.toleranceYounger) parts.push(`až o ${rule.toleranceYounger} ${plural(rule.toleranceYounger, ['rok', 'roky', 'let'])} mladší`);
  else parts.push(`nikdo mladší než ${rule.min} ${plural(rule.min, ['rok', 'roky', 'let'])}`);
  if (rule.toleranceOlder && rule.max != null) {
    parts.push(`o ${rule.toleranceOlder} ${plural(rule.toleranceOlder, ['rok', 'roky', 'let'])} starší`);
  }
  return `${range}; tolerance: ${parts.join(' nebo ')}, nejvýše polovina tanečníků`;
}

const yearOf = (value) => {
  const year = Number(String(value || '').slice(0, 4));
  return Number.isFinite(year) && year > 0 ? year : new Date().getFullYear();
};

/** Age in the competition year, as the backend counts it (year difference). */
export const ageInCompetitionYear = (birthDate, competitionDate) => yearOf(competitionDate) - yearOf(birthDate);

export const yearsLabel = (age) => `${age} ${plural(age, ['rok', 'roky', 'let'])}`;

const normalise = (text) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/** 'direct' | 'tolerance' | 'invalid' for one dancer in the category (same table as the server). */
export function ageStatus(age, ageCategory, rules = DEFAULT_AGE_RULES) {
  const rule = (rules && rules.categories && rules.categories[ageCategory]) || DEFAULT_AGE_RULES.categories[ageCategory];
  if (!rule) return 'direct';
  if (age >= rule.min && (rule.max == null || age <= rule.max)) return 'direct';
  if (age < rule.min && age >= rule.min - (rule.toleranceYounger || 0)) return 'tolerance';
  if (rule.max != null && age > rule.max && age <= rule.max + (rule.toleranceOlder || 0)) return 'tolerance';
  return 'invalid';
}

/**
 * Check a selection against the rules.
 * Returns { valid, count, toleranceCount, problems: [Czech sentences] }.
 */
export function evaluateSelection(dancers, selectedIds, ageCategory, competitionDate, rules = DEFAULT_AGE_RULES) {
  const byId = new Map((dancers || []).map((d) => [d.id, d]));
  const selected = (selectedIds || []).map((id) => byId.get(id)).filter(Boolean);
  const problems = [];
  const invalid = [];
  let toleranceCount = 0;

  selected.forEach((d) => {
    const age = ageInCompetitionYear(d.birthDate, competitionDate);
    const status = ageStatus(age, ageCategory, rules);
    if (status === 'invalid') invalid.push(`${d.firstName} ${d.lastName} (${yearsLabel(age)})`);
    if (status === 'tolerance') toleranceCount += 1;
  });

  const count = selected.length;
  if (count < MIN_DANCERS) {
    problems.push(`Vyberte alespoň ${MIN_DANCERS} tanečníky (zatím ${count}).`);
  }
  if (count > MAX_DANCERS) {
    problems.push(`Choreografie může mít nejvýše ${MAX_DANCERS} tanečníků (vybráno ${count}).`);
  }
  if (invalid.length > 0) {
    problems.push(`Mimo věkový rozsah kategorie ${ageCategoryLabel(ageCategory)}: ${invalid.join(', ')}.`);
  }
  const maxShare = (rules && rules.maxToleranceShare) || 0.5;
  if (count > 0 && toleranceCount / count > maxShare) {
    problems.push(
      `Věkovou toleranci využívá víc než polovina vybraných tanečníků (${toleranceCount} z ${count}). Povoleno je nejvýše 50 %.`
    );
  }
  return { valid: problems.length === 0, count, toleranceCount, problems };
}

const GROUPS = [
  { key: 'invalid', title: 'Vybraní, ale věkem do kategorie nepatří – odeberte je', onlySelected: true },
  { key: 'direct', title: 'Věkem odpovídají kategorii' },
  { key: 'tolerance', title: 'S věkovou tolerancí' },
];

export default function DancerPicker({
  dancers,
  competitionDate,
  ageCategory,
  value,
  onChange,
  showSummary = true,
  rules: rulesProp,
}) {
  const meta = useMeta();
  const rules = rulesProp || meta.ageRules || DEFAULT_AGE_RULES;
  const [query, setQuery] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);
  const selected = useMemo(() => new Set(value || []), [value]);

  const rows = useMemo(
    () =>
      (dancers || []).map((d) => {
        const age = ageInCompetitionYear(d.birthDate, competitionDate);
        return { ...d, age, status: ageStatus(age, ageCategory, rules) };
      }),
    [dancers, competitionDate, ageCategory, rules]
  );

  const evaluation = useMemo(
    () => evaluateSelection(dancers, value, ageCategory, competitionDate, rules),
    [dancers, value, ageCategory, competitionDate, rules]
  );
  // 2.6: dancers who cannot dance in the category are not offered at all.
  const hiddenIneligible = rows.filter((d) => d.status === 'invalid' && !selected.has(d.id)).length;

  const q = normalise(query.trim());
  const visible = rows.filter(
    (d) =>
      (!q || normalise(`${d.firstName} ${d.lastName}`).includes(q) || normalise(`${d.lastName} ${d.firstName}`).includes(q)) &&
      (!onlySelected || selected.has(d.id))
  );
  const atMax = selected.size >= MAX_DANCERS;

  const toggle = (dancer) => {
    const isSelected = selected.has(dancer.id);
    if (!isSelected && (dancer.status === 'invalid' || atMax)) return;
    const next = isSelected ? (value || []).filter((id) => id !== dancer.id) : [...(value || []), dancer.id];
    onChange(next);
  };

  const countTone = evaluation.count < MIN_DANCERS || evaluation.count > MAX_DANCERS ? 'text-red-700' : 'text-green-700';
  const tolerancePercent = evaluation.count ? Math.round((evaluation.toleranceCount / evaluation.count) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* -top-4 covers the modal body padding, so nothing scrolls visibly above the bar */}
      <div className="sticky -top-4 z-10 -mt-3 space-y-3 bg-white pb-2 pt-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-gray-700" aria-live="polite">
            <span className={`font-semibold ${countTone}`}>Vybráno {evaluation.count}</span>{' '}
            <span className="text-gray-600">
              (min. {MIN_DANCERS}, max. {MAX_DANCERS})
            </span>
          </p>
          <button
            type="button"
            aria-pressed={onlySelected}
            onClick={() => setOnlySelected((v) => !v)}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
              onlySelected ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {onlySelected ? 'Zobrazit všechny' : `Jen vybraní (${evaluation.count})`}
          </button>
        </div>
        <label className="relative block">
          <span className="sr-only">Hledat tanečníka</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Hledat podle jména…"
            className="block w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </label>
      </div>

      {ageCategory && (
        <p className="text-sm text-gray-600">
          Kategorie {ageCategoryLabel(ageCategory)}: {ruleText(ageCategory, rules)} (věk v roce soutěže).
          {evaluation.toleranceCount > 0 && ` Toleranci teď využívá ${evaluation.toleranceCount} z ${evaluation.count} (${tolerancePercent} %).`}
          {hiddenIneligible > 0 &&
            ` ${hiddenIneligible} ${plural(hiddenIneligible, ['tanečník do kategorie věkem nepatří', 'tanečníci do kategorie věkem nepatří', 'tanečníků do kategorie věkem nepatří'])}, proto ${plural(hiddenIneligible, ['není', 'nejsou', 'nejsou'])} v nabídce.`}
        </p>
      )}

      {rows.length === 0 && (
        <p className="rounded-md bg-gray-50 p-4 text-sm text-gray-700">
          Ve skupině zatím nemáte žádné tanečníky. Přidejte je na stránce Tanečníci.
        </p>
      )}
      {rows.length > 0 && visible.length === 0 && (
        <p className="rounded-md bg-gray-50 p-4 text-sm text-gray-700">Žádný tanečník neodpovídá hledání.</p>
      )}

      {GROUPS.map((group) => {
        const items = visible.filter((d) => d.status === group.key && (!group.onlySelected || selected.has(d.id)));
        if (items.length === 0) return null;
        return (
          <fieldset key={group.key} className="space-y-2">
            <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
              {group.title} ({items.length})
            </legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((d) => {
                const isSelected = selected.has(d.id);
                const isInvalid = d.status === 'invalid';
                const disabled = !isSelected && (isInvalid || atMax);
                let tone = 'border-gray-200 bg-white hover:border-gray-400';
                if (isSelected && isInvalid) tone = 'border-red-500 bg-red-50';
                else if (isSelected) tone = 'border-indigo-500 bg-indigo-50';
                else if (disabled) tone = 'border-gray-200 bg-gray-50 opacity-60';
                return (
                  <label
                    key={d.id}
                    className={`flex items-center gap-3 rounded-lg border p-3 ${tone} ${
                      disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                    } focus-within:ring-2 focus-within:ring-indigo-500`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={disabled}
                      onChange={() => toggle(d)}
                      className="h-5 w-5 flex-shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-gray-900">
                        {d.firstName} {d.lastName}
                      </span>
                      <span className="block text-sm text-gray-600">
                        {yearsLabel(d.age)}
                        {d.status === 'tolerance' && <span className="ml-1 text-amber-800">· věková tolerance</span>}
                        {isInvalid && <span className="ml-1 text-red-700">· mimo věkový rozsah</span>}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        );
      })}

      {showSummary && evaluation.count > 0 && !evaluation.valid && (
        <div role="alert" className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <ul className="space-y-1">
            {evaluation.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
